import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';

const CustomCursor = ({
  speed = 0.7,
  ease = "expo.out",
  visibleTimeout = 300,
}) => {
  const cursorRef = useRef(null);
  const textRef = useRef(null);
  const [pos, setPos] = useState({ x: -window.innerWidth, y: -window.innerHeight });
  const [visible, setVisible] = useState(false);
  const [stick, setStick] = useState(null);
  const visibleTimeoutRef = useRef(null);

  useEffect(() => {
    const el = cursorRef.current;
    const text = textRef.current;

    const updatePos = (e) => {
      const newPos = stick
        ? {
            x: stick.x - (stick.x - e.clientX) * 0.15,
            y: stick.y - (stick.y - e.clientY) * 0.15,
          }
        : { x: e.clientX, y: e.clientY };
      setPos(newPos);
      moveCursor(newPos.x, newPos.y);
      showCursor();
    };

    const setCursorText = (textContent) => {
      if (text) {
        text.innerHTML = textContent;
        el.classList.add('-text');
      }
    };

    const removeCursorText = () => {
      el.classList.remove('-text');
    };

    const setCursorStick = (selector) => {
      const target = document.querySelector(selector);
      if (target) {
        const rect = target.getBoundingClientRect();
        setStick({
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        });
        moveCursor(rect.left + rect.width / 2, rect.top + rect.height / 2, 5);
      }
    };

    const removeCursorStick = () => {
      setStick(null);
    };

    const moveCursor = (x, y, duration = speed) => {
      gsap.to(el, {
        x: x || pos.x,
        y: y || pos.y,
        force3D: true,
        overwrite: true,
        ease,
        duration: visible ? duration : 0,
      });
    };

    const showCursor = () => {
      if (visible) return;
      clearTimeout(visibleTimeoutRef.current);
      el.classList.add('-visible');
      visibleTimeoutRef.current = setTimeout(() => setVisible(true), 0);
    };

    const hideCursor = () => {
      clearTimeout(visibleTimeoutRef.current);
      el.classList.remove('-visible');
      visibleTimeoutRef.current = setTimeout(() => setVisible(false), visibleTimeout);
    };

    const onMouseEnterElement = (e) => {
      const { cursor, cursorText, cursorStick } = e.target.dataset;
      if (cursor) el.classList.add(cursor);
      if (cursorText) setCursorText(cursorText);
      if (cursorStick) setCursorStick(cursorStick);
    };

    const onMouseLeaveElement = (e) => {
      const { cursor, cursorText, cursorStick } = e.target.dataset;
      if (cursor) el.classList.remove(cursor);
      if (cursorText) removeCursorText();
      if (cursorStick) removeCursorStick();
    };

    // Add all event listeners
    const container = document.body;
    container.addEventListener('mousemove', updatePos);
    container.addEventListener('mouseleave', hideCursor);
    container.addEventListener('mouseenter', showCursor);
    container.addEventListener('mousedown', () => el.classList.add('-active'));
    container.addEventListener('mouseup', () => el.classList.remove('-active'));
    container.addEventListener('mouseenter', pointerOn, true);
    container.addEventListener('mouseleave', pointerOff, true);
    container.querySelectorAll('[data-cursor], [data-cursor-text], [data-cursor-stick]').forEach(elm => {
      elm.addEventListener('mouseenter', onMouseEnterElement);
      elm.addEventListener('mouseleave', onMouseLeaveElement);
    });

    function pointerOn(e) {
      const tag = e.target.tagName.toLowerCase();
      if (['a', 'button', 'input', 'textarea'].includes(tag)) {
        el.classList.add('-pointer');
      }
      if (tag === 'iframe') hideCursor();
    }

    function pointerOff(e) {
      const tag = e.target.tagName.toLowerCase();
      if (['a', 'button', 'input', 'textarea'].includes(tag)) {
        el.classList.remove('-pointer');
      }
      if (tag === 'iframe') showCursor();
    }

    // Cleanup
    return () => {
      container.removeEventListener('mousemove', updatePos);
      container.removeEventListener('mouseleave', hideCursor);
      container.removeEventListener('mouseenter', showCursor);
      container.removeEventListener('mousedown', () => el.classList.add('-active'));
      container.removeEventListener('mouseup', () => el.classList.remove('-active'));
      container.removeEventListener('mouseenter', pointerOn, true);
      container.removeEventListener('mouseleave', pointerOff, true);
      container.querySelectorAll('[data-cursor], [data-cursor-text], [data-cursor-stick]').forEach(elm => {
        elm.removeEventListener('mouseenter', onMouseEnterElement);
        elm.removeEventListener('mouseleave', onMouseLeaveElement);
      });
    };
  }, [stick, visible, pos, ease, speed, visibleTimeout]);

  return (
    <div className="cb-cursor" ref={cursorRef}>
      <div className="cb-cursor-text" ref={textRef}></div>
    </div>
  );
};

export default CustomCursor;
